import { useState, useRef } from "react";
import { Voucher } from "@/lib/accounting/types";
import { renderInvoiceHTML, InvoiceTemplate, TEMPLATE_OPTIONS } from "./InvoiceTemplates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Download, Mail, Eye, MessageCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { WhatsAppSendDialog } from "./WhatsAppInvoiceSender";

interface InvoicePreviewDialogProps {
  voucher: Voucher;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InvoicePreviewDialog({ voucher, open, onOpenChange }: InvoicePreviewDialogProps) {
  const [template, setTemplate] = useState<InvoiceTemplate>("gst_standard");
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { toast } = useToast();

  const html = renderInvoiceHTML(voucher, template);

  const handlePrint = () => {
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => { win.print(); }, 500);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${voucher.invoiceNumber || voucher.voucherNumber}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded", description: `Invoice saved as ${a.download}` });
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Invoice ${voucher.invoiceNumber || voucher.voucherNumber} from ABC Trading Co.`);
    const body = encodeURIComponent(`Dear ${voucher.partyName || "Customer"},\n\nPlease find attached invoice ${voucher.invoiceNumber || voucher.voucherNumber} for ₹${voucher.totalDebit.toLocaleString("en-IN")}.\n\nRegards,\nABC Trading Co.`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
    toast({ title: "Email Client Opened", description: "Compose your email with the invoice details" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Eye className="h-5 w-5 text-accent" />
            Invoice Preview — {voucher.invoiceNumber || voucher.voucherNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Template:</span>
            <Select value={template} onValueChange={(v) => setTemplate(v as InvoiceTemplate)}>
              <SelectTrigger className="h-8 w-44 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_OPTIONS.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="text-sm">
                    <div>
                      <span className="font-medium">{t.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">— {t.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => setWhatsAppOpen(true)} className="gap-1.5 text-success border-success/30 hover:bg-success/10">
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </Button>
          <Button variant="outline" size="sm" onClick={handleEmail} className="gap-1.5">
            <Mail className="h-3.5 w-3.5" /> Email
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5">
            <Download className="h-3.5 w-3.5" /> Download
          </Button>
          <Button size="sm" onClick={handlePrint} className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90">
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
        </div>

        <div className="flex-1 overflow-hidden rounded-lg border border-border bg-muted/30">
          <iframe
            ref={iframeRef}
            srcDoc={html}
            className="w-full h-[600px] bg-white rounded-lg"
            title="Invoice Preview"
          />
        </div>
      </DialogContent>
      {whatsAppOpen && (
        <WhatsAppSendDialog voucher={voucher} open={whatsAppOpen} onOpenChange={setWhatsAppOpen} />
      )}
    </Dialog>
  );
}
